// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import { useCallback, useEffect, useState } from "react";

import { mdiAlert } from "@mdi/js";
import { Select } from "antd";
import _debounce from "lodash/debounce";
import styled from "styled-components";

import { BinType } from "models";
import { QuantileType } from "models/quantileType";

import SegmentedToggle, { SegmentedVariant } from "components/shared/SegmentedToggle";
import { DefaultBinSettings as DefaultBinSettingsModel } from "components/user-settings/models";
import { useUserContext } from "components/user/context";
import { updateSettingBinSettings } from "components/user/context/reducer/UserReducer";
import { useUserDefaults, useUserSettingMutation } from "components/user/hooks";
import { useUserSettingsDefaultsQuery } from "components/user/queries";

import {
  SubsectionHeadingLabel,
  USER_SETTINGS_BIN_TYPE_SELECT,
  USER_SETTINGS_NUMBER_OF_QUANTILES_INPUT,
  USER_SETTINGS_QUANTILE_TYPE_SELECT
} from "../../../../constants";
import { DEFAULT_BIN_SETTINGS_SETTING } from "../../constants/userSetting.constants";
import {
  ErrorContainer,
  SectionContainer,
  SectionHeading,
  SectionHeadingTitleContainer,
  SectionInputWrapper,
  StyledNumberInput,
  SubsectionContainer,
  SubsectionHeading
} from "./shared";

const quantileTypeDisplayMap = {
  NumberOfQuantile: "# of Quantile",
  IQR: "IQR"
};

const DefaultBinSettings = () => {
  // hooks
  const [, dispatch] = useUserContext();
  const defaults = useUserDefaults();
  const mutation = useUserSettingMutation<DefaultBinSettingsModel>(
    DEFAULT_BIN_SETTINGS_SETTING
  );
  const { refetch } = useUserSettingsDefaultsQuery();

  const binType = defaults?.binSettings;

  // state
  const [currentDefaultBinSettings, setCurrentDefaultBinSettings] = useState(binType);

  const [updateError, setUpdateError] = useState("");
  const debouncedSettingsChange = _debounce((settings: DefaultBinSettingsModel) => {
    mutation.mutate(settings);
  }, 400);

  const updateBinSettings = useCallback(
    (settings: DefaultBinSettingsModel) => {
      debouncedSettingsChange({
        id: binType?.id,
        type: settings.type,
        quantile: {
          quantileType: settings.quantile.quantileType,
          numQuantiles: settings.quantile.numQuantiles
        },
        useDynamicBins: settings.useDynamicBins
      });
      refetch();
    },
    [binType?.id]
  );

  const handleDefaultBinTypeChange = useCallback(
    (type: BinType) => {
      let newType = type;
      // Show type as dynamic but function as bin size besides useDynamicBins
      if (type === "Dynamic") {
        newType = "BinSize";
      }
      const newSettings = {
        ...currentDefaultBinSettings,
        type: newType,
        quantile: {
          quantileType: "NumberOfQuantile" as QuantileType,
          numQuantiles: 4
        },
        useDynamicBins: type === "Dynamic"
      };
      setCurrentDefaultBinSettings(newSettings);
      updateBinSettings(newSettings);
    },
    [
      currentDefaultBinSettings?.type,
      currentDefaultBinSettings?.quantile?.quantileType,
      currentDefaultBinSettings?.quantile?.numQuantiles,
      currentDefaultBinSettings?.useDynamicBins,
      updateBinSettings
    ]
  );

  const handleDefaultQuantileTypeChange = useCallback(
    (type: QuantileType) => {
      const newSettings = {
        ...currentDefaultBinSettings,
        quantile: {
          quantileType: type,
          numQuantiles: currentDefaultBinSettings?.quantile?.numQuantiles
        }
      };
      setCurrentDefaultBinSettings(newSettings);
      updateBinSettings(newSettings);
    },
    [
      currentDefaultBinSettings?.type,
      currentDefaultBinSettings?.quantile?.quantileType,
      currentDefaultBinSettings?.quantile?.numQuantiles,
      currentDefaultBinSettings?.useDynamicBins,
      updateBinSettings
    ]
  );

  const handleNumberOfQuantilesChanged = useCallback(
    (value: number) => {
      const newSettings = {
        ...currentDefaultBinSettings,
        quantile: {
          quantileType: currentDefaultBinSettings?.quantile?.quantileType,
          numQuantiles: value
        }
      };
      setCurrentDefaultBinSettings(newSettings);
      updateBinSettings(newSettings);
    },
    [
      currentDefaultBinSettings?.type,
      currentDefaultBinSettings?.quantile?.quantileType,
      currentDefaultBinSettings?.quantile?.numQuantiles,
      currentDefaultBinSettings?.useDynamicBins,
      updateBinSettings
    ]
  );
  // Handle reload
  useEffect(() => {
    if (!mutation?.data?.id) return;

    updateSettingBinSettings(dispatch, {
      id: mutation.data.id,
      type: mutation.data.type,
      quantile: mutation.data.quantile,
      useDynamicBins: mutation.data.useDynamicBins
    });
  }, [mutation?.data]);

  // Handle reload
  useEffect(() => {
    if (mutation?.error) {
      // eslint-disable-next-line no-console
      console.error(mutation?.error);
      setUpdateError("Failed to update bin settings.");
    } else {
      setUpdateError("");
    }
  }, [mutation?.error]);

  return (
    <SectionContainer data-testid="default-bin-settings">
      <SectionHeading>
        <SectionHeadingTitleContainer>
          Bin Settings
          {updateError && (
            <ErrorContainer>
              <Icon path={mdiAlert} size={1} /> {updateError}
            </ErrorContainer>
          )}
        </SectionHeadingTitleContainer>
      </SectionHeading>

      <SectionInputWrapper>
        <BinContentContainer>
          <SubsectionContainer>
            <SubsectionHeading>{SubsectionHeadingLabel.BinType}</SubsectionHeading>
            <SectionInputWrapper>
              <StyledSelect
                data-testid={USER_SETTINGS_BIN_TYPE_SELECT}
                value={
                  currentDefaultBinSettings?.type === "BinSize" &&
                  currentDefaultBinSettings?.useDynamicBins
                    ? "Dynamic"
                    : currentDefaultBinSettings?.type
                }
                onChange={handleDefaultBinTypeChange}
                options={["Bin Size", "Quantile", "Dynamic"].map((item) => ({
                  value: item.replace(" ", ""),
                  label: item
                }))}
              />
            </SectionInputWrapper>
          </SubsectionContainer>

          <SubsectionContainer>
            <SubsectionHeading>{SubsectionHeadingLabel.QuantileType}</SubsectionHeading>

            <SectionInputWrapper>
              <SegmentedToggle
                variant={SegmentedVariant.Base}
                height={50}
                data-testid={USER_SETTINGS_QUANTILE_TYPE_SELECT}
                value={
                  quantileTypeDisplayMap[
                    currentDefaultBinSettings?.quantile?.quantileType
                  ]
                }
                onChange={(value) => {
                  const quantileType =
                    value === "# of Quantile" ? "NumberOfQuantile" : "IQR";
                  handleDefaultQuantileTypeChange(quantileType as QuantileType);
                }}
                options={["# of Quantile", "IQR"]}
                disabled={currentDefaultBinSettings?.type !== "Quantile"}
              />
            </SectionInputWrapper>
          </SubsectionContainer>
          <SubsectionContainer>
            <SubsectionHeading>Number of Quantiles</SubsectionHeading>
            <SectionInputWrapper>
              <StyledNumberInput
                data-testid={USER_SETTINGS_NUMBER_OF_QUANTILES_INPUT}
                step={1}
                value={currentDefaultBinSettings?.quantile?.numQuantiles}
                onChange={handleNumberOfQuantilesChanged}
                disabled={
                  currentDefaultBinSettings?.type !== "Quantile" ||
                  currentDefaultBinSettings?.quantile?.quantileType !==
                    ("NumberOfQuantile" as QuantileType)
                }
              />
            </SectionInputWrapper>
          </SubsectionContainer>
        </BinContentContainer>
      </SectionInputWrapper>
    </SectionContainer>
  );
};

export default DefaultBinSettings;

export const StyledSelect = styled(Select)`
  height: 50px;
  display: flex;
  align-items: center;

  .ant-select-selector {
    display: flex;
    align-items: center;
    height: 100% !important;
  }
`;

const BinContentContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(200px, 1fr));
  gap: 8px 24px;

  @media screen and (max-width: 1280px) {
    grid-template-columns: 1fr;
  }
`;
